import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { WintSuperkollAPIModelTimeReporting as APIModel } from "wint-api-client/dist/types";
import { TimeReportInvoiceStatusProps } from "models/propTypes/timeReportingPropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { DialogEnums, StyleEnums } from "models/enums";
import { SelectionItem } from "models/utilTypes/selectionTypes";
//#endregion
//#region components
import { Icon } from "components/common";
import { Badge, Grid, Tooltip } from "@mui/material";
import InvoicePickerItem from "./invoicePickerItem";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import clsx from "clsx";
import { TranslationKeys } from "translations";

const InvoiceStatus: React.FC<TimeReportInvoiceStatusProps> = (props) => {
  const location = useLocation();
  const navigate = useNavigate();

  const getInvoiceStatusIcon = (status?: APIModel.TimeReportInvoiceStatus) => {
    switch (status) {
      case APIModel.TimeReportInvoiceStatus.Unhandled:
        return {};
      case APIModel.TimeReportInvoiceStatus.Draft:
        return { type: StyleEnums.DisplayType.Warning, icon: IconDefinitionConstants.WPaperPlane };
      case APIModel.TimeReportInvoiceStatus.Sent:
        return { type: StyleEnums.DisplayType.Success, icon: IconDefinitionConstants.WPaperPlane };
      case APIModel.TimeReportInvoiceStatus.Credited:
        return { type: StyleEnums.DisplayType.Danger, icon: IconDefinitionConstants.WPaperPlane };
      case APIModel.TimeReportInvoiceStatus.PartlyCredited:
        return { type: StyleEnums.DisplayType.Info, icon: IconDefinitionConstants.WPaperPlane };
      case APIModel.TimeReportInvoiceStatus.PartlyConnectedToInvoices:
        return { type: StyleEnums.DisplayType.Warning, icon: IconDefinitionConstants.WPaperPlane };
      case APIModel.TimeReportInvoiceStatus.AllConnectedToInvoices:
        return { type: StyleEnums.DisplayType.Info, icon: IconDefinitionConstants.WPaperPlane };
      default:
        return {};
    }
  };

  const iconValue = getInvoiceStatusIcon(props.status);

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const getInvoiceItems = (searchString: string): Promise<SelectionItem[]> => {
    return Promise.resolve(
      props.invoices
        ? props.invoices
            ?.filter((invoiceInfo) => !searchString || invoiceInfo.serialNumber.toString().includes(searchString))
            .map(
              (invoiceInfo) =>
                ({
                  id: invoiceInfo.invoiceId,
                  value: invoiceInfo.serialNumber.toString(),
                  data: invoiceInfo,
                  icon: IconDefinitionConstants.WPaperPlane,
                } as SelectionItem)
            )
        : []
    );
  };

  const onClick = (e?: React.MouseEvent) => {
    if (props.invoices && props.invoices.length > 1) {
      e?.stopPropagation();
      dispatch(
        displayDialog({
          isOpen: false,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.InvoicePickerDialogTitle),
          titleIcon: IconDefinitionConstants.WPaperPlane,
          subTitle: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReporting.InvoicePickerDialogSubtitle
          ),
          dialogType: DialogEnums.DialogType.Selection,
          children: {
            getSuggestions: () => getInvoiceItems(""),
            search: getInvoiceItems,
            multiSelection: false,
            geSelectiontItemTemplate: (data) => {
              return <InvoicePickerItem invoiceInfo={data} />;
            },
            tabs: [
              {
                getResultList: getInvoiceItems,
                title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ConnectedInvoices),
                searchInputPlaceholder: LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.TimeReporting.SearchForInvoiceNumber
                ),
                hasSearch: true,
              },
            ],
          },
          onSubmit: (values: any) => {
            navigate(UrlHelper.formatPath(`/Invoice/${values.id}`), {
              state: { background: location },
            });
          },
        })
      );
    } else if (props.invoiceId) {
      e?.stopPropagation();
      navigate(UrlHelper.formatPath(`/Invoice/${props.invoiceId}`), {
        state: { background: location },
      });
    } else if (props.status === APIModel.TimeReportInvoiceStatus.Credited) {
      e?.stopPropagation();
      const lastInvoice =
        props.invoices && props.invoices.length > 0 ? props.invoices[props.invoices.length - 1] : undefined;
      navigate(UrlHelper.formatPath(`/Invoice/${lastInvoice?.invoiceId}`), {
        state: { background: location },
      });
    }
  };

  const getTooltip = () => {
    const status = `TimeReporting.InvoiceStatus_${props.status}`;
    let serialNumber: number | undefined;

    if (props.invoiceSerialNumber) {
      serialNumber = props.invoiceSerialNumber;
    } else if (props.status === APIModel.TimeReportInvoiceStatus.Credited) {
      const lastInvoice =
        props.invoices && props.invoices.length > 0 ? props.invoices[props.invoices.length - 1] : undefined;
      serialNumber = lastInvoice?.serialNumber;
    }
    return LocalizationHelperInstance.getFormattedMessage(status, "", {
      invoiceSerialNumber: serialNumber ? `${serialNumber}` : "",
    });
  };

  return iconValue.icon ? (
    <Tooltip
      disableInteractive
      placement="top"
      title={getTooltip()}
      classes={{ tooltip: cssExports.tooltip, popper: cssExports.tooltipPopper }}
    >
      <Grid item onClick={onClick} className={clsx(cssExports.cursorPointer)}>
        {(props?.invoices?.length ?? 0) > 1 ? (
          <Badge
            badgeContent={props?.invoices?.length}
            classes={{ badge: cssExports.smallBadge }}
            max={99}
            overlap="circular"
          >
            <Icon icon={iconValue.icon} displayType={iconValue.type} />
          </Badge>
        ) : (
          <Icon icon={iconValue.icon} displayType={iconValue.type} />
        )}
      </Grid>
    </Tooltip>
  ) : (
    <></>
  );
};

export default InvoiceStatus;
